import {
  ActivityIndicator, Alert, Modal, Pressable, StyleSheet, ScrollView,
  Text, TextInput, View, KeyboardAvoidingView, Platform,
  Keyboard,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'react-native-markdown-display';
import Moment from 'moment';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated';
import { FontAwesome } from '@expo/vector-icons';
import Style from '../../constants/Style';
import BotIcon60 from '../../components/60pxIcons/BotIcon60';
import Colors from '../../constants/Colors';
import getChatbotSessions from '../../requests/Chatbot/GetChatbotSessions';
import sendChatMessage from '../../requests/Chatbot/SendChatMessage';
import getSessionMessages from '../../requests/Chatbot/GetSessionMessages';
import isMobile from '../../components/IsMobile';
import RefreshIcon from '../../components/16pxIcons/RefreshIcon';
import BinIcon from '../../components/BinIcon';
import EditIcon24 from '../../components/24pxIcons/EditIcon24';
import archiveChatbotSession from '../../requests/Chatbot/ArchiveChatbotSession';
import PressableStyled from '../../components/PressableStyled';
import EllipsisIconVertical from '../../components/EllipsisIconVertical';
import BinIcon12 from '../../components/12pxIcons/BinIcon12';
import deleteChatbotSession from '../../requests/Chatbot/DeleteChatbotSession';
import AlertModal from '../../components/AlertModal';
import unarchiveChatbotSession from '../../requests/Chatbot/UnarchiveChatbotSession';
import LineGraph from '../../graphs/LineGraph';
import BarGraph from '../../graphs/BarGraph';
import ChildrenBarGraph from '../../graphs/ChildrenBarGraph';
import LineGraphMobile from '../../graphs/LineGraphMobile';
import PieChart from '../../graphs/PieChart';

export default function ChatbotScreen() {
  const [sessions, setSessions] = useState([]);
  const [reloadCount, setReloadCount] = useState(0);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [LastMessageSent, setLastMessageSent] = useState(Moment());
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatHistoryModalVisible, setChatHistoryModalVisible] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [archivingSession, setArchivingSession] = useState(false);
  const [deletingSession, setDeletingSession] = useState(false);
  const [viewArchivedSessions, setViewArchivedSessions] = useState(false);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const scrollViewRef = useRef<any>(null);

  const buildingId = useSelector(
    (state: { selectedBuilding: { id: string } }) => state.selectedBuilding.id,
  );

  useEffect(() => {
    const fetchChatbotSessions = async () => {
      setLoadingSessions(true);
      const response = await getChatbotSessions(viewArchivedSessions);
      setSessions(response ?? []);
      setLoadingSessions(false);
    };
    fetchChatbotSessions();
  }, [reloadCount, viewArchivedSessions]);

  useEffect(() => {
    if (selectedSession) {
      setLoadingMessages(true);
      setErrorMessage('');
      getSessionMessages(selectedSession.sessionId).then((messages) => {
        setSelectedSession({ ...selectedSession, messages });
        setLoadingMessages(false);
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      });
    }
  }, [selectedSession?.sessionId, LastMessageSent]);

  function RightAction(prog: SharedValue<number>, drag: SharedValue<number>) {
    const styleAnimation = useAnimatedStyle(() => ({
      transform: [{ translateX: drag.value + 30 }],
    }));

    return (
      <Animated.View style={styleAnimation}>
        <Pressable
          onPress={() => Alert.alert('Coming soon!')}
          style={{
            width: 30, height: 40, justifyContent: 'center', alignItems: 'center',
          }}>
          <BinIcon />
        </Pressable>
      </Animated.View>
    );
  }

  const handleSendMessage = async () => {
    if (message.trim() === '') return;
    Keyboard.dismiss();
    setSendingMessage(true);
    setErrorMessage('');
    sendChatMessage(message, buildingId, selectedSession?.sessionId).then((session) => {
      if (session.sessionId && selectedSession?.sessionId
          !== session.sessionId) {
        setSelectedSession(session);
        setReloadCount(reloadCount + 1);
        if (viewArchivedSessions) setViewArchivedSessions(false);
      } else setLastMessageSent(Moment());
    }).catch(() => {
      setErrorMessage('Something went wrong while sending your message. Please try again.');
    }).finally(() => {
      setMessage('');
      setSendingMessage(false);
    });
  };

  const Sessions = () => {
    if (loadingSessions) {
      return <Text>Loading...</Text>;
    }
    if (sessions?.length === 0) {
      return <Text>No chat sessions found.</Text>;
    }
    return (
      <ScrollView>
        {sessions.map((session: any) => (
          <Swipeable
            renderRightActions={RightAction}
            enabled={isMobile}
            key={session.sessionId}
          >
          <Pressable
            key={session.sessionId}
            disabled={selectedSession?.sessionId === session.sessionId}
            onPress={() => {
              setSelectedSession(session);
              setChatHistoryModalVisible(false);
            }}
            style={{
              padding: 10,
              borderWidth: 1,
              borderColor: '#eee',
              borderRadius: 8,
              marginBottom: 10,
              backgroundColor: selectedSession?.sessionId === session.sessionId
                ? Colors.light.hereworksBlue500 : 'white',
            }}>
            <Text
              style={{
                fontSize: 14,
                fontFamily: 'Poppins_400Regular',
                color: selectedSession?.sessionId === session.sessionId
                  ? '#fff' : Colors.light.hereworksBlack500,
              }}>{session.sessionName}</Text>
          </Pressable>
          </Swipeable>
        ))}
      </ScrollView>
    );
  };

  type ChartData =
    | Array<{ value: number; timestamp?: string; date?: string; label?: string }>
    | { line1?: { lineChartData?: Array<{ value: number; date: string }> } }
    | {
        bar1?: Array<{ value: number; label?: string }>;
        bar2?: Array<{ value: number; label?: string }>;
      };

  const Chart = ({ data, type }: { data: ChartData; type: string }) => {
    let chartData = [];
    if (Array.isArray(data)) {
      chartData = data.map((item, index) => ({
        index,
        value: item.value,
        date: item.timestamp ?? item.date,
      }));
    } else if ((data as any)?.line1?.lineChartData) {
      chartData = (data as any).line1.lineChartData.map((item: any, index: number) => ({
        index,
        value: item.value,
        date: item.date,
      }));
    }

    if (type === 'BAR_CHART') {
      let barChartData = [];
      if (Array.isArray(data)) {
        barChartData = data.map((item) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (isMobile) {
        return <ScrollView horizontal>
                  <ChildrenBarGraph
                    data={barChartData}
                    compareToggled={false}
                    comparisonData={[]} />
                </ScrollView>;
      }
      return <BarGraph chartData={chartData} title='Value' />;
    }
    if (type === 'LINE_CHART') {
      if (isMobile) {
        return <View style={{ aspectRatio: 1 }}>
                  <LineGraphMobile
                    data={Array.isArray(data)
                      ? data.map((point, index: number) => ({
                        value: point.value, date: point.date, index,
                      }))
                      : []}
                    comparisonData={[]} />
                </View>;
      }
      return (<LineGraph chartData={chartData} noTitle noAxisLabel title='Value' />);
    }
    if (type === 'PIE_CHART') {
      return (
        <PieChart
          chartData={
            Array.isArray(data)
              ? data.map((item, index) => ({
                index,
                value: item.value,
                date: item.timestamp ?? item.date ?? '',
                label: item.label ?? '',
              }))
              : []
          }
        />
      );
    }

    if (type === 'COMPARISON_LINE_CHART') {
      let line1Data: { index: number; value: number; date: string }[] = [];
      let line2Data: { index: number; value: number; date: string }[] = [];
      if (
        typeof data === 'object'
        && data !== null
        && 'line1' in data
        && (data as any).line1?.lineChartData
      ) {
        line1Data = (data as any).line1.lineChartData.map((item: any, index: number) => ({
          index,
          value: item.value,
          date: item.date,
        }));
      }
      if (
        typeof data === 'object'
        && data !== null
        && 'line2' in data
        && (data as any).line2?.lineChartData
      ) {
        line2Data = (data as any).line2.lineChartData.map((item: any, index: number) => ({
          index,
          value: item.value,
          date: item.date,
        }));
      }

      if (isMobile) {
        return <View style={{ aspectRatio: 1 }}>
                  <LineGraphMobile
                    data={line1Data ?? []}
                    comparisonData={[]} />
                </View>;
      }
      return (<LineGraph
        chartData={line1Data}
        comparisonData={line2Data ?? []}
        noTitle
        noAxisLabel
        title='Value'
      />);
    }
    if (type === 'COMPARISON_BAR_CHART') {
      let bar1Data = [];
      let bar2Data = [];
      if (
        typeof data === 'object'
        && data !== null
        && 'bar1' in data
        && Array.isArray((data as any).bar1?.barChartData)
      ) {
        bar1Data = (data as any).bar1.barChartData.map((item: any) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (
        typeof data === 'object'
        && data !== null
        && 'bar2' in data
        && Array.isArray((data as any).bar2?.barChartData)
      ) {
        bar2Data = (data as any).bar2.barChartData.map((item: any) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (isMobile) {
        return <ScrollView horizontal>
                  <ChildrenBarGraph
                    data={bar1Data}
                    compareToggled={false}
                    comparisonData={bar2Data} />
                </ScrollView>;
      }
      return <BarGraph
        chartData={bar1Data}
        comparisonData={bar2Data}
        title='Value'
      />;
    }

    return null;
  };

  return <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
    >
  <View style={[Style.row,
    { flex: 1, marginHorizontal: isMobile ? 24 : 40, marginVertical: isMobile ? 18 : 32 }]}>
    <View style={{
      flex: 3,
      backgroundColor: 'white',
      alignSelf: 'stretch',
      justifyContent: 'flex-start',
      borderRadius: 10,
    }}>
      {isMobile ? <View style={Style.row}>
        <Pressable onPress={() => setChatHistoryModalVisible(true)} style={{
          padding: 8,
          flexDirection: 'row',
          alignItems: 'center',
        }}>
          <RefreshIcon />
          <Text style={{
            color: Colors.light.hereworksBlue500,
            fontFamily: 'Poppins_500Medium',
            marginLeft: 4,
          }}>Chat History</Text>
        </Pressable>
        {selectedSession ? <Pressable
          style={{
            marginLeft: 'auto', padding: 8, flexDirection: 'row', alignItems: 'center',
          }}
          onPress={() => {
            setSelectedSession(null);
            setChatHistoryModalVisible(false);
          }}>
            <EditIcon24 />
          <Text style={{
            color: Colors.light.hereworksBlue500,
            fontFamily: 'Poppins_500Medium',
            marginLeft: 4,
          }}>New session</Text>
        </Pressable> : null}
      </View> : null}
      {isMobile ? null : <View style={{
        borderBottomWidth: 1,
        borderBottomColor: Colors.light.hereworksWhite200,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 21,
      }}>
          <Text style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 18,
            paddingHorizontal: 20,
            flex: 1,
          }}>{selectedSession?.sessionName ?? 'New Chat'}</Text>
          {selectedSession ? <>
          <Pressable
            style={{
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: Colors.light.hereworksBlue500,
              marginRight: 12,
            }}
            disabled={archivingSession}
            onPress={() => {
              setArchivingSession(true);
              if (viewArchivedSessions) {
                unarchiveChatbotSession(selectedSession.sessionId).then(() => {
                  setSelectedSession(null);
                  setReloadCount(reloadCount + 1);
                  setArchivingSession(false);
                });
              } else {
                archiveChatbotSession(selectedSession.sessionId).then(() => {
                  setSelectedSession(null);
                  setReloadCount(reloadCount + 1);
                  setArchivingSession(false);
                });
              }
            }}>
            <FontAwesome name="archive" size={16} color={Colors.light.hereworksBlue500} />
            {(archivingSession || deletingSession) ? <View
            style={[StyleSheet.absoluteFillObject,
              {
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#00000020',
                borderRadius: 8,
              },
            ]}>
              <ActivityIndicator
              style={{ }}
              size="small"
              color={Colors.light.hereworksBlue500}
            /></View> : null}
          </Pressable>
          {viewArchivedSessions && <Pressable
            style={{
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: Colors.light.hereworksRed300,
              marginRight: 12,
            }} onPress={() => {
              setDeletingSession(true);
            }}>
              <BinIcon12 />
          </Pressable>}
          <Pressable
            style={{
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 8,
              marginRight: 24,
              backgroundColor: Colors.light.hereworksBlue500,
            }}
            onPress={() => {
              setSelectedSession(null);
              setErrorMessage('');
              setChatHistoryModalVisible(false);
            }}>
            <Text style={{
              color: '#fff',
              fontSize: 14,
              lineHeight: 18,
              fontFamily: 'Roboto_400Regular',
            }}>New Session</Text>
          </Pressable></> : null}
        </View>}
      {selectedSession ? <ScrollView
        ref={scrollViewRef}
        style={{
          flex: 1,
          marginBottom: 20,
          padding: isMobile ? 12 : 20,
        }}
        contentContainerStyle={{
          flexGrow: 1, paddingBottom: 20, justifyContent: 'flex-end',
        }}>
        {selectedSession.messages?.length === 0
          && <Text>No messages found.</Text>}
        {(selectedSession.messages ?? []).sort((a, b) => new Date(a.timestamp).getTime()
          - new Date(b.timestamp).getTime()).map((msg: any, index: number) => (
          <View key={index} style={{ marginBottom: 12, alignSelf: 'stretch' }}>
            <View style={{
              borderWidth: 1,
              borderColor: Colors.light.hereworksWhite300,
              padding: 8,
              borderRadius: 8,
              marginLeft: '20%',
              marginBottom: 16,
            }}>
              <View style={{ flexDirection: 'row', marginBottom: 4, alignItems: 'baseline' }}>
                <Text style={{
                  fontFamily: 'Poppins_600SemiBold',
                  fontSize: 10,
                }}>You</Text>
                <Text style={{
                  fontSize: 10,
                  color: Colors.light.hereworksBlack400,
                  marginLeft: 8,
                }}>{Moment(msg?.timestamp).format('DD MMM YYYY, h:mm A')}</Text>
              </View>
              <Text style={{
                fontFamily: 'Poppins_400Regular',
              }}>{msg?.userMessage}</Text>
            </View>
            <View style={{
              marginRight: '20%',
              backgroundColor: Colors.light.hereworksWhite300,
              padding: 8,
              borderRadius: 8,
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 4 }}>
                <Text style={{
                  fontFamily: 'Poppins_600SemiBold',
                  fontSize: 10,
                }}>Chatbot</Text>
                <Text style={{
                  fontSize: 10,
                  color: Colors.light.hereworksBlack400,
                  marginLeft: 8,
                }}>{Moment(msg?.timestamp).format('DD MMM YYYY, h:mm A')}</Text>
              </View>
              <Markdown>{msg?.chatbotResponse?.message}</Markdown>
              {(Array.isArray(msg.chatbotResponse?.uiWidgetMetaData?.data)
                || Array.isArray(msg.chatbotResponse?.uiWidgetMetaData?.data?.line1?.lineChartData))
                ? <Chart data={msg.chatbotResponse.uiWidgetMetaData.data}
                type={msg.chatbotResponse.uiWidgetMetaData.widgetType} /> : null}
            </View>
          </View>
        ))}
      </ScrollView> : <ScrollView style={{
      }} contentContainerStyle={{
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <LinearGradient
          colors={['#2BC3A510', '#85E1CF10']}
          style={{ padding: 13, borderRadius: 76 }}
        >
        <LinearGradient
          colors={['#2BC3A550', '#85E1CF50']}
          style={{ padding: 13, borderRadius: 76 }}
        >
          <LinearGradient
            colors={['#2BC3A5', '#85E1CF']}
            style={{ padding: 20, borderRadius: 50 }}
          >
            <BotIcon60 />
          </LinearGradient>
        </LinearGradient>
        </LinearGradient>
        <Text style={{
          fontFamily: 'Poppins_400Regular',
          fontSize: 24,
          lineHeight: 36,
          textAlign: 'center',
          marginBottom: 24,
        }}>Welcome to Happy Chat</Text>
      </ScrollView>}
      {sendingMessage ? <View style={{
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 10,
      }}>
        <ActivityIndicator size='large' color={Colors.light.hereworksBlue500} />
        <Text
          style={{
            marginTop: 12,
            fontFamily: 'Poppins_400Regular',
            fontSize: 16,
            color: Colors.light.hereworksBlack400,
          }}
        >Thinking...</Text>
      </View> : null}
      {errorMessage ? <View style={{
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 10,
        padding: 12,
        backgroundColor: '#FFEEEE',
        marginHorizontal: 20,
        marginBottom: 10,
      }}>
        <Text
          style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 16,
            color: '#D8000C',
            textAlign: 'center',
          }}
        >{errorMessage}</Text>
      </View> : null}
      <View style={[Style.row, { padding: isMobile ? 12 : 20 }]}>
        <TextInput
          placeholder="Ask a question..."
          placeholderTextColor={Colors.light.hereworksBlack400}
          style={{
            flex: 1,
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 12,
            borderColor: '#E0E0E0',
            borderRadius: 8,
          }}
          value={message}
          onChangeText={(text) => setMessage(text)}
          returnKeyType='send'
          submitBehavior='blurAndSubmit'
          onSubmitEditing={() => handleSendMessage()}
        />
        <Pressable
          style={{
            backgroundColor: Colors.light.hereworksBlue500,
            opacity: sendingMessage || message.trim() === '' ? 0.5 : 1,
            borderRadius: 8,
            padding: 12,
            marginLeft: 12,
            alignSelf: 'center',
          }}
          disabled={sendingMessage || message.trim() === ''}
          onPress={() => handleSendMessage()}
        >
          <Text style={{
            color: 'white',
            fontFamily: 'Poppins_500Medium',
            textAlign: 'center',
          }}>Send</Text>
        </Pressable>
      </View>
      {loadingMessages ? <View style={[StyleSheet.absoluteFill,
        { justifyContent: 'center', backgroundColor: '#00000005' }]}>
        <ActivityIndicator size='large' color={Colors.light.hereworksBlue500} />
      </View> : null}
      <AlertModal
        modalVisible={deletingSession}
        title={'Deleting chat session'}
        message={'Are you sure you want to delete this chat session? This action cannot be undone.'}
        action={() => {
          deleteChatbotSession(selectedSession.sessionId).then(() => {
            setDeletingSession(false);
            setSelectedSession(null);
            setReloadCount(reloadCount + 1);
          });
        }}
        actionTitle='Delete'
        onModalHide={() => setDeletingSession(false)}
        setModalVisible={setDeletingSession}
      />
    </View>
    {isMobile ? null : <View style={{
      flex: 1,
      backgroundColor: 'white',
      alignSelf: 'stretch',
      padding: 20,
      borderRadius: 10,
      marginStart: 20,
    }}>
      <View style={[Style.row, { marginBottom: 20, zIndex: 10 }]}>
        <Text>{viewArchivedSessions ? 'Archived Chats' : 'Chat History'}</Text>
        <PressableStyled onPress={() => {
          setContextMenuVisible((prev) => !prev);
        }}>
          <EllipsisIconVertical />
        </PressableStyled>
        {contextMenuVisible && <View style={{
          position: 'absolute',
          top: 30,
          right: 0,
          backgroundColor: 'white',
          borderWidth: 1,
          borderColor: '#ccc',
          borderRadius: 8,
          zIndex: 10,
        }}>
          <PressableStyled
            style={{
              padding: 12,
            }}
            onPress={() => {
              setReloadCount(reloadCount + 1);
              setContextMenuVisible(false);
            }}>
            <Text style={{
              color: Colors.light.hereworksBlue500,
              fontFamily: 'Poppins_500Medium',
            }}>Reload Chats</Text>
          </PressableStyled>
          <PressableStyled
            style={{
              padding: 12,
              marginLeft: 'auto',
            }}
            onPress={() => {
              setViewArchivedSessions((prev) => !prev);
              setSelectedSession(null);
              setContextMenuVisible(false);
            }}>
            <Text style={{
              color: Colors.light.hereworksBlue500,
              fontFamily: 'Poppins_500Medium',
            }}>{viewArchivedSessions ? 'View Chats' : 'View Archived'}</Text>
          </PressableStyled>
        </View>}
      </View>
      <Sessions />
    </View>}
    <Modal
      visible={chatHistoryModalVisible}
      onRequestClose={() => setChatHistoryModalVisible(false)}
      animationType="slide"
      presentationStyle={'formSheet'}
    >
      <View style={{ flex: 1, padding: 20, backgroundColor: 'white' }}>
        <View style={[Style.row, { marginBottom: 20 }]}>
          <Text style={{ fontSize: 18, fontWeight: 'bold' }}>Chat History</Text>
          <Pressable
            style={{ marginLeft: 'auto' }}
            onPress={() => {
              setReloadCount(reloadCount + 1);
            }}>
            <Text style={{
              color: Colors.light.hereworksBlue500,
              fontFamily: 'Poppins_500Medium',
            }}>Reload</Text>
          </Pressable>
        </View>
        <Sessions />
      </View>
    </Modal>
  </View>
  </KeyboardAvoidingView>;
}
